/* =====================================================================================================================
   DESIGNERS SHOP THE LOOK — isolated behaviour
   ---------------------------------------------------------------------------------------------------------------
   Self-contained vanilla JavaScript. No imports, no import map, no vendor library, no `window.themeVariables`,
   no theme custom element, no shadow DOM template.

   Registers exactly one custom element: <dstl-shop-the-look>. Every instance owns its own state, so any number of
   sections can live on the same page. Registration and per-instance initialisation are both guarded, and every
   listener is bound through an AbortController so Theme Editor reloads leave nothing behind.

   Mobile full screen layer: a native <dialog> opened with showModal(). It renders in the browser top layer, which
   means it paints above the site header without this section ever selecting, styling or moving the header.
   ===================================================================================================================== */

(function () {
  'use strict';

  var TAG_NAME = 'dstl-shop-the-look';

  if (!window.customElements || window.customElements.get(TAG_NAME)) {
    return;
  }

  var DESKTOP_QUERY = '(min-width: 1000px)';
  var EASE_IN_OUT_CUBIC = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
  var SCROLL_END_FALLBACK_DELAY = 90;

  function prefersMotion() {
    return window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
  }

  function isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  function padTwo(value) {
    var text = String(value);
    return text.length >= 2 ? text : '0' + text;
  }

  /* -------------------------------------------------------------------------------------------------------------
     Page scroll lock — reference counted and fully restored, so several sections (or an unexpected double open)
     can never leave the page stuck.
     ------------------------------------------------------------------------------------------------------------- */

  var scrollLockCount = 0;
  var savedOverflow = null;

  function lockPageScroll() {
    if (scrollLockCount === 0) {
      savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }
    scrollLockCount += 1;
  }

  function unlockPageScroll() {
    if (scrollLockCount === 0) {
      return;
    }
    scrollLockCount -= 1;
    if (scrollLockCount === 0) {
      if (savedOverflow) {
        document.documentElement.style.overflow = savedOverflow;
      } else {
        document.documentElement.style.removeProperty('overflow');
      }
      savedOverflow = null;
    }
  }

  /* -------------------------------------------------------------------------------------------------------------
     Minimal Web Animations helper. Replaces the theme's Motion One `animate` / `timeline`.
     Durations collapse to 0 when the visitor asks for reduced motion.
     ------------------------------------------------------------------------------------------------------------- */

  function animateElements(targets, keyframes, options) {
    var list = targets ? (targets.nodeType ? [targets] : toArray(targets)) : [];
    var settings = options || {};
    var motion = prefersMotion();

    var animations = list
      .map(function (element) {
        if (!element || typeof element.animate !== 'function') {
          return null;
        }
        try {
          return element.animate(keyframes, {
            duration: motion ? settings.duration || 0 : 0,
            delay: motion ? settings.delay || 0 : 0,
            easing: settings.easing || 'ease',
            fill: 'both'
          });
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean);

    return {
      finished: Promise.all(
        animations.map(function (animation) {
          return animation.finished.catch(function () {});
        })
      ),
      /**
       * True while any of these animations still affects rendering. `fill: 'both'` means a FINISHED animation
       * keeps holding its end styles and keeps overriding inline styles, so "finished" is NOT "done with" —
       * only a cancelled ('idle') animation has stopped applying. Tracking must use this, otherwise a finished
       * fade-out is pruned from the tracking list and its `opacity: 0` can never be cleared again.
       */
      isHoldingStyles: function () {
        return animations.some(function (animation) {
          return animation.playState !== 'idle';
        });
      },
      cancel: function () {
        animations.forEach(function (animation) {
          try {
            animation.cancel();
          } catch (error) {
            /* already detached */
          }
        });
      }
    };
  }

  /* -------------------------------------------------------------------------------------------------------------
     Scroll helpers. Everything is expressed in viewport coordinates and driven through `scrollBy`, which keeps the
     maths identical in LTR and RTL (no `scrollLeft` sign handling, no `offsetLeft` guesswork).
     ------------------------------------------------------------------------------------------------------------- */

  function centerOf(rect) {
    return rect.left + rect.width / 2;
  }

  function closestCellIndex(container, cells) {
    if (!container || !cells.length) {
      return 0;
    }
    var target = centerOf(container.getBoundingClientRect());
    var best = 0;
    var bestDistance = Infinity;

    cells.forEach(function (cell, index) {
      var distance = Math.abs(centerOf(cell.getBoundingClientRect()) - target);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    return best;
  }

  function scrollCellIntoView(container, cell, smooth) {
    if (!container || !cell) {
      return;
    }
    var delta = centerOf(cell.getBoundingClientRect()) - centerOf(container.getBoundingClientRect());
    if (Math.abs(delta) < 1) {
      return;
    }
    container.scrollBy({
      left: delta,
      behavior: smooth && prefersMotion() ? 'smooth' : 'auto'
    });
  }

  function onScrollSettled(container, signal, callback) {
    var supportsScrollEnd = 'onscrollend' in window;
    var timer = null;

    if (supportsScrollEnd) {
      container.addEventListener('scrollend', callback, { signal: signal });
      return;
    }

    container.addEventListener(
      'scroll',
      function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(callback, SCROLL_END_FALLBACK_DELAY);
      },
      { signal: signal, passive: true }
    );
  }

  /* =============================================================================================================
     THE ELEMENT
     ============================================================================================================= */

  class ShopTheLookSection extends HTMLElement {
    connectedCallback() {
      // Guard against double initialisation (re-connection, Theme Editor DOM moves, ...).
      if (this._dstlInitialised) {
        return;
      }

      this._strip = this.querySelector('[data-dstl-strip]');
      this._stage = this.querySelector('[data-dstl-stage]');
      this._modal = this.querySelector('[data-dstl-modal]');
      this._modalStage = this.querySelector('[data-dstl-modal-stage]');
      this._modalSheet = this.querySelector('[data-dstl-modal-sheet]');

      this._mobileLooks = this._strip ? toArray(this._strip.querySelectorAll('[data-dstl-look]')) : [];
      this._desktopLooks = this._stage ? toArray(this._stage.querySelectorAll('[data-dstl-look]')) : [];
      this._panels = this._modal ? toArray(this._modal.querySelectorAll('[data-dstl-panel]')) : [];

      this._roomTabs = toArray(this.querySelectorAll('[data-dstl-room-tab]'));
      this._roomPanel = this.querySelector('[data-dstl-room-panel]');
      this._counter = this.querySelector('[data-dstl-counter]');

      this._lookCount = Math.max(this._desktopLooks.length, this._mobileLooks.length);
      if (this._lookCount === 0) {
        return;
      }

      this._dstlInitialised = true;
      this._abortController = new AbortController();
      this._signal = this._abortController.signal;

      this._lookIndex = 0;
      this._productIndex = new Array(this._lookCount).fill(0);

      // ---- rooms -------------------------------------------------------------------------------------------
      // `_lookIndex` stays a GLOBAL index into the three index-aligned arrays above, so nothing in the look /
      // product / modal logic has to know about rooms. `_visible` is the ordered list of global indices that
      // belong to the active room; only navigation and the counter read it.
      var referenceLooks = this._desktopLooks.length ? this._desktopLooks : this._mobileLooks;
      this._roomOf = referenceLooks.map(function (look) {
        return look.getAttribute('data-dstl-room') || '';
      });
      this._activeRoom = '';
      this._visible = [];
      this._counterTemplate = this._counter
        ? this._counter.getAttribute('data-dstl-counter-template') || ''
        : '';
      this._transitionToken = 0;
      this._running = [];
      this._stageAnimation = null;
      this._stageTranslateY = 0;
      this._modalOpen = false;
      this._modalClosing = false;
      this._previousFocus = null;
      this._supportsDialog = !!(this._modal && typeof this._modal.showModal === 'function');

      this.classList.add('dstl--js');
      this.setAttribute('data-dstl-dir', getComputedStyle(this).direction === 'rtl' ? 'rtl' : 'ltr');

      this._bindInteractions();
      this._bindRoomTabs();
      this._bindStripScroll();
      this._bindModal();
      this._bindThemeEditor();
      this._bindViewport();

      // Establish the initial state without any animation, then arm the reveal.
      // The server already rendered one room as active; adopt it so markup and JS agree.
      var initialTab = this._roomTabs.filter(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      })[0];
      var initialRoom = initialTab
        ? initialTab.getAttribute('data-dstl-room')
        : this._roomOf.length
        ? this._roomOf[0]
        : '';

      this._selectRoom(initialRoom, { instant: true });
      this._bindReveal();
    }

    disconnectedCallback() {
      this._teardown();
    }

    /* ----------------------------------------------------------------------------------------------------------
       Cleanup — everything this section ever touched is undone here.
       ---------------------------------------------------------------------------------------------------------- */

    _teardown() {
      if (!this._dstlInitialised) {
        return;
      }
      this._dstlInitialised = false;

      this._cancelRunning();

      if (this._modalOpen) {
        this._forceCloseModal();
      }

      if (this._modalStage) {
        this._modalStage.replaceChildren();
        this._modalStage.style.removeProperty('transform');
      }
      if (this._modalSheet) {
        this._modalSheet.style.removeProperty('transform');
      }
      if (this._modal) {
        this._modal.style.removeProperty('opacity');
      }

      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
      if (this._intersectionObserver) {
        this._intersectionObserver.disconnect();
        this._intersectionObserver = null;
      }

      this.classList.remove('dstl--js');
      this.removeAttribute('data-dstl-dir');
    }

    _cancelRunning() {
      this._running.forEach(function (controls) {
        controls.cancel();
      });
      this._running = [];

      if (this._stageAnimation) {
        this._stageAnimation.cancel();
        this._stageAnimation = null;
      }
    }

    _track(controls) {
      // Drop only animations that have been cancelled; anything still holding a fill must stay cancellable.
      this._running = this._running.filter(function (entry) {
        return entry.isHoldingStyles();
      });
      this._running.push(controls);
      return controls;
    }

    /* ----------------------------------------------------------------------------------------------------------
       Event wiring
       ---------------------------------------------------------------------------------------------------------- */

    _bindInteractions() {
      var self = this;

      this.addEventListener(
        'click',
        function (event) {
          var target = event.target;
          if (!target || !target.closest) {
            return;
          }

          var hotspot = target.closest('[data-dstl-hotspot]');
          if (hotspot) {
            self._onHotspotClicked(hotspot);
            return;
          }

          var roomTab = target.closest('[data-dstl-room-tab]');
          if (roomTab) {
            self._selectRoom(roomTab.getAttribute('data-dstl-room'), {});
            return;
          }

          var dot = target.closest('[data-dstl-dot]');
          if (dot) {
            self._selectProduct(self._lookIndex, parseInt(dot.getAttribute('data-dstl-product-index'), 10) || 0, {});
            return;
          }

          if (target.closest('[data-dstl-open]')) {
            self._openModal(self._lookIndex);
            return;
          }

          if (target.closest('[data-dstl-close]')) {
            self._closeModal();
            return;
          }

          if (target.closest('[data-dstl-prev]')) {
            self._stepLook(-1);
            return;
          }

          if (target.closest('[data-dstl-next]')) {
            self._stepLook(1);
          }
        },
        { signal: this._signal }
      );

      // Keyboard navigation on the desktop stage, mirroring the original carousel's ArrowLeft / ArrowRight.
      if (this._stage && this._lookCount > 1) {
        this._stage.tabIndex = 0;
        this._stage.addEventListener(
          'keydown',
          function (event) {
            if (event.target !== self._stage || !isDesktop()) {
              return;
            }
            if (event.code === 'ArrowLeft') {
              event.preventDefault();
              self._stepLook(-1);
            } else if (event.code === 'ArrowRight') {
              event.preventDefault();
              self._stepLook(1);
            }
          },
          { signal: this._signal }
        );
      }
    }

    _onHotspotClicked(hotspot) {
      var productIndex = parseInt(hotspot.getAttribute('data-dstl-product-index'), 10) || 0;
      var inModal = !!(this._modalStage && this._modalStage.contains(hotspot));
      var look = hotspot.closest('[data-dstl-look]');
      var lookIndex = inModal ? this._lookIndex : parseInt(look && look.getAttribute('data-dstl-look-index'), 10) || 0;

      if (!inModal && lookIndex !== this._lookIndex) {
        this._selectLook(lookIndex, { instant: true });
      }

      this._selectProduct(lookIndex, productIndex, {});

      if (inModal) {
        // Re-centre the focused look on the newly selected hot spot, exactly like the original did.
        this._applyStageTranslate(this._computeStageTranslate(), true);
      } else if (!isDesktop()) {
        // On mobile, tapping a hot spot opens the product interface (original behaviour).
        this._openModal(lookIndex);
      }
    }

    _bindStripScroll() {
      if (!this._strip || this._mobileLooks.length < 2) {
        return;
      }
      var self = this;
      onScrollSettled(this._strip, this._signal, function () {
        if (self._modalOpen) {
          return;
        }
        // Only the looks of the active room are in the strip; map the settled position back to a global index.
        var cells = self._visibleMobileLooks();
        if (!cells.length) {
          return;
        }
        var index = self._visible[closestCellIndex(self._strip, cells)];
        if (index !== undefined && index !== self._lookIndex) {
          self._selectLook(index, { instant: true, fromStripScroll: true });
        }
      });
    }

    _bindViewport() {
      var self = this;
      window.addEventListener(
        'resize',
        function () {
          if (self._modalOpen) {
            self._applyStageTranslate(self._computeStageTranslate(), false);
          }
        },
        { signal: this._signal, passive: true }
      );
    }

    _bindReveal() {
      var self = this;

      if (!this._stage || typeof IntersectionObserver === 'undefined') {
        return;
      }

      this._intersectionObserver = new IntersectionObserver(
        function (entries) {
          var visible = entries.some(function (entry) {
            return entry.isIntersecting;
          });
          if (!visible) {
            return;
          }
          if (self._intersectionObserver) {
            self._intersectionObserver.disconnect();
            self._intersectionObserver = null;
          }
          self._revealLookBody(self._desktopLooks[self._lookIndex]);
        },
        { rootMargin: '0px 0px -10% 0px' }
      );

      this._intersectionObserver.observe(this._stage);
    }

    _revealLookBody(look) {
      if (!look) {
        return;
      }
      var body = look.querySelector('[data-dstl-look-body]');
      if (!body) {
        return;
      }
      this._track(
        animateElements(body, { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 500 })
      );
    }

    _bindThemeEditor() {
      if (!window.Shopify || !window.Shopify.designMode) {
        return;
      }

      var self = this;

      // `shopify:block:select` bubbles from the look element that carries `block.shopify_attributes`.
      this.addEventListener(
        'shopify:block:select',
        function (event) {
          var look = event.target && event.target.closest ? event.target.closest('[data-dstl-look]') : null;
          if (!look) {
            return;
          }
          var index = parseInt(look.getAttribute('data-dstl-look-index'), 10) || 0;
          var instant = !!(event.detail && event.detail.load);
          var room = look.getAttribute('data-dstl-room') || '';

          // Selecting a block in a different room must switch to that room first, then reveal the look.
          if (room && room !== self._activeRoom) {
            self._selectRoom(room, { instant: instant, keepLook: index });
          } else {
            self._selectLook(index, { instant: instant });
          }
        },
        { signal: this._signal }
      );

      var sectionElement = this.closest('.shopify-section');
      if (sectionElement) {
        sectionElement.addEventListener(
          'shopify:section:unload',
          function () {
            self._teardown();
          },
          { signal: this._signal }
        );
      }
    }


    /* ----------------------------------------------------------------------------------------------------------
       ROOMS + LOOK COUNTER
       ----------------------------------------------------------------------------------------------------------
       Rooms are a filter layer on top of the existing implementation. Looks stay exactly where they are in the DOM
       and are simply given the `hidden` attribute when they do not belong to the active room, which the mobile
       scroll strip and the desktop stacked grid both collapse without any layout change.
       ---------------------------------------------------------------------------------------------------------- */

    _visibleMobileLooks() {
      var looks = this._mobileLooks;
      return this._visible
        .map(function (index) {
          return looks[index];
        })
        .filter(Boolean);
    }

    /** Rebuild `_visible` for the active room and apply the `hidden` attribute to everything outside it. */
    _applyRoomFilter() {
      var self = this;
      var visible = [];

      this._roomOf.forEach(function (room, index) {
        var inRoom = !self._activeRoom || room === self._activeRoom;

        if (inRoom) {
          visible.push(index);
        }
        if (self._mobileLooks[index]) {
          self._mobileLooks[index].toggleAttribute('hidden', !inRoom);
        }
        if (self._desktopLooks[index]) {
          self._desktopLooks[index].toggleAttribute('hidden', !inRoom);
        }
      });

      this._visible = visible;

      // Arrows cannot do anything in a single-look room; disable them so the state is announced, not just inert.
      var disabled = visible.length < 2;
      toArray(this.querySelectorAll('[data-dstl-prev], [data-dstl-next]')).forEach(function (button) {
        button.disabled = disabled;
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      });
    }

    /**
     * Switch room: filter the looks, jump to the first look of the room, reset that look to its first product,
     * and refresh the tab state and the counter. `keepLook` is used by the Theme Editor so that selecting a block
     * in another room lands on that exact block instead of the room's first one.
     */
    _selectRoom(roomId, options) {
      var settings = options || {};
      var self = this;

      if (!roomId) {
        roomId = this._roomOf.length ? this._roomOf[0] : '';
      }
      if (this._roomOf.indexOf(roomId) === -1 && this._roomOf.length) {
        return;
      }

      this._activeRoom = roomId;
      this._applyRoomFilter();

      this._roomTabs.forEach(function (tab) {
        var active = tab.getAttribute('data-dstl-room') === roomId;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        tab.classList.toggle('is-dstl-active', active);
        // Presentation only: the destination theme's outline variant marks the unselected tabs.
        tab.classList.toggle('button--outline', !active);

        if (active && self._roomPanel && tab.id) {
          self._roomPanel.setAttribute('aria-labelledby', tab.id);
        }
      });

      if (!this._visible.length) {
        this._updateCounter();
        return;
      }

      var target = this._visible.indexOf(settings.keepLook) !== -1 ? settings.keepLook : this._visible[0];

      // Entering a room always starts from that look's first product.
      this._productIndex[target] = 0;
      this._lookIndex = target;
      this._selectLook(target, { instant: true });
      this._selectProduct(target, 0, { instant: true });
      this._updateCounter();
    }

    /** Move through the ACTIVE ROOM only. A room with a single look is a no-op. */
    _stepLook(delta) {
      if (this._visible.length < 2) {
        return;
      }
      var position = this._visible.indexOf(this._lookIndex);
      if (position === -1) {
        position = 0;
      }
      var next = (position + delta + this._visible.length) % this._visible.length;
      this._selectLook(this._visible[next], {});
    }

    _updateCounter() {
      if (!this._counter) {
        return;
      }

      var total = this._visible.length;
      var position = this._visible.indexOf(this._lookIndex);
      var current = position === -1 ? (total ? 1 : 0) : position + 1;

      var activeTab = this._roomTabs.find(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      });

      var roomName = activeTab
        ? activeTab.getAttribute('data-dstl-room-name') || ''
        : '';

      this._counter.textContent = this._counterTemplate
        .replace('[room]', roomName)
        .replace('[current]', padTwo(current))
        .replace('[total]', padTwo(total));
    }

    _bindRoomTabs() {
      if (!this._roomTabs.length) {
        return;
      }
      var self = this;

      this.addEventListener(
        'keydown',
        function (event) {
          var tab = event.target && event.target.closest ? event.target.closest('[data-dstl-room-tab]') : null;
          if (!tab) {
            return;
          }

          var tabs = self._roomTabs;
          var index = tabs.indexOf(tab);
          var rtl = self.getAttribute('data-dstl-dir') === 'rtl';
          var next = -1;

          switch (event.key) {
            case 'ArrowRight':
              next = rtl ? index - 1 : index + 1;
              break;
            case 'ArrowLeft':
              next = rtl ? index + 1 : index - 1;
              break;
            case 'Home':
              next = 0;
              break;
            case 'End':
              next = tabs.length - 1;
              break;
            default:
              return;
          }

          next = (next + tabs.length) % tabs.length;
          event.preventDefault();
          tabs[next].focus();
          self._selectRoom(tabs[next].getAttribute('data-dstl-room'), {});
        },
        { signal: this._signal }
      );
    }

    /* ----------------------------------------------------------------------------------------------------------
       Look selection
       ---------------------------------------------------------------------------------------------------------- */

    _selectLook(index, options) {
      var settings = options || {};
      index = clamp(index, 0, this._lookCount - 1);

      // Never select a look that the active room filter has hidden.
      if (this._visible.length && this._visible.indexOf(index) === -1) {
        return Promise.resolve();
      }

      var previousIndex = this._lookIndex;
      var changed = index !== previousIndex;
      this._lookIndex = index;

      // Covers every path: room switch, desktop arrows, arrow keys, mobile scroll settle, Theme Editor selection.
      this._updateCounter();

      // Mobile: keep the strip aligned (unless the change came from the strip itself).
      if (this._strip && !settings.fromStripScroll) {
        scrollCellIntoView(this._strip, this._mobileLooks[index], !settings.instant);
      }

      this._mobileLooks.forEach(function (look, i) {
        look.classList.toggle('is-dstl-selected', i === index);
      });

      var from = this._desktopLooks[previousIndex];
      var to = this._desktopLooks[index];

      if (!to) {
        return Promise.resolve();
      }

      if (!changed || settings.instant || !from || !isDesktop()) {
        this._cancelRunning();
        this._desktopLooks.forEach(function (look, i) {
          look.classList.toggle('is-dstl-selected', i === index);
          var body = look.querySelector('[data-dstl-look-body]');
          if (body) {
            body.style.opacity = i === index ? '1' : '';
          }
          toArray(look.querySelectorAll('[data-dstl-media] > *')).forEach(function (child) {
            child.style.removeProperty('opacity');
            child.style.removeProperty('transform');
          });
        });
        this._syncProductState(index);
        return Promise.resolve();
      }

      return this._transitionLooks(from, to, index);
    }

    /**
     * Reproduces the original desktop timeline:
     *   0.0s  outgoing body      opacity 1 -> 0                                        (0.3s)
     *   0.0s  outgoing media     opacity 1 -> 0, translateX 0 -> -15px                 (0.5s)
     *   0.0s  incoming media     opacity 0 -> 1, translateX -15px -> 0                 (0.5s)
     *   0.4s  incoming body      opacity 0 -> 1, translateY 10px -> 0                  (0.5s)
     * The 15px offset is mirrored in RTL so the movement always follows the reading direction.
     */
    _transitionLooks(from, to, index) {
      var self = this;
      var token = ++this._transitionToken;

      this._cancelRunning();

      var offset = this.getAttribute('data-dstl-dir') === 'rtl' ? 15 : -15;
      var fromBody = from.querySelector('[data-dstl-look-body]');
      var toBody = to.querySelector('[data-dstl-look-body]');
      var fromMedia = from.querySelectorAll('[data-dstl-media] > *');
      var toMedia = to.querySelectorAll('[data-dstl-media] > *');

      // Both looks are visible for the duration of the cross-fade.
      to.classList.add('is-dstl-selected');
      this._syncProductState(index);

      var controls = [
        this._track(animateElements(fromBody, { opacity: [1, 0] }, { duration: 300 })),
        this._track(
          animateElements(
            fromMedia,
            { opacity: [1, 0], transform: ['translateX(0)', 'translateX(' + offset + 'px)'] },
            { duration: 500, easing: EASE_IN_OUT_CUBIC }
          )
        ),
        this._track(
          animateElements(
            toMedia,
            { opacity: [0, 1], transform: ['translateX(' + offset + 'px)', 'translateX(0)'] },
            { duration: 500 }
          )
        ),
        this._track(
          animateElements(
            toBody,
            { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] },
            { duration: 500, delay: 400 }
          )
        )
      ];

      return Promise.all(
        controls.map(function (control) {
          return control.finished;
        })
      ).then(function () {
        if (token !== self._transitionToken) {
          return;
        }
        from.classList.remove('is-dstl-selected');
      });
    }

    /* ----------------------------------------------------------------------------------------------------------
       Product selection + hot spot synchronisation
       ---------------------------------------------------------------------------------------------------------- */

    _hotspotRoots(lookIndex) {
      var roots = [];
      if (this._mobileLooks[lookIndex]) {
        roots.push(this._mobileLooks[lookIndex]);
      }
      if (this._desktopLooks[lookIndex]) {
        roots.push(this._desktopLooks[lookIndex]);
      }
      if (this._modalOpen && this._modalStage && lookIndex === this._lookIndex) {
        roots.push(this._modalStage);
      }
      return roots;
    }

    _selectProduct(lookIndex, productIndex, options) {
      var settings = options || {};
      var reference = this._mobileLooks[lookIndex] || this._desktopLooks[lookIndex];
      if (!reference) {
        return;
      }

      var total = reference.querySelectorAll('[data-dstl-hotspot]').length || 1;
      productIndex = clamp(productIndex, 0, total - 1);
      this._productIndex[lookIndex] = productIndex;

      this._hotspotRoots(lookIndex).forEach(function (root) {
        toArray(root.querySelectorAll('[data-dstl-hotspot]')).forEach(function (hotspot) {
          var index = parseInt(hotspot.getAttribute('data-dstl-product-index'), 10) || 0;
          hotspot.setAttribute('aria-current', index === productIndex ? 'true' : 'false');
        });
      });

      var desktopLook = this._desktopLooks[lookIndex];
      if (desktopLook) {
        toArray(desktopLook.querySelectorAll('[data-dstl-dot]')).forEach(function (dot) {
          var index = parseInt(dot.getAttribute('data-dstl-product-index'), 10) || 0;
          dot.setAttribute('aria-current', index === productIndex ? 'true' : 'false');
        });

        this._crossfadeDesktopProducts(desktopLook, productIndex, settings.instant);
        this._updateCallToAction(desktopLook, productIndex);
      }

      var panel = this._panels[lookIndex];
      if (panel) {
        var cells = toArray(panel.querySelectorAll('[data-dstl-product-item]'));
        cells.forEach(function (cell, index) {
          cell.classList.toggle('is-dstl-selected', index === productIndex);
        });

        if (this._modalOpen && lookIndex === this._lookIndex && !settings.fromProductScroll) {
          scrollCellIntoView(panel.querySelector('[data-dstl-products]'), cells[productIndex], !settings.instant);
        }
      }
    }

    _syncProductState(lookIndex) {
      this._selectProduct(lookIndex, this._productIndex[lookIndex] || 0, { instant: true });
    }

    _crossfadeDesktopProducts(look, productIndex, instant) {
      var products = toArray(look.querySelectorAll('[data-dstl-product-item]'));
      if (!products.length) {
        return;
      }

      var to = products[productIndex];
      var from = products.filter(function (product) {
        return product !== to && product.classList.contains('is-dstl-selected');
      });

      if (instant || !isDesktop() || !from.length) {
        products.forEach(function (product, index) {
          product.classList.toggle('is-dstl-selected', index === productIndex);
          product.style.removeProperty('opacity');
        });
        return;
      }

      to.classList.add('is-dstl-selected');

      var leaving = this._track(animateElements(from, { opacity: [1, 0] }, { duration: 300 }));
      var entering = this._track(animateElements(to, { opacity: [0, 1] }, { duration: 300 }));

      Promise.all([leaving.finished, entering.finished]).then(function () {
        from.forEach(function (product) {
          product.classList.remove('is-dstl-selected');
        });
      });
    }

    /**
     * The CTA always mirrors the currently selected card's `data-dstl-url`, which is supplied by the adapter.
     * Called from `_selectProduct`, so it runs on initialisation, hot spot selection, dot selection and look change.
     *
     * When the selected card has no URL (placeholder card, or an adapter that could not resolve one) the link is
     * disabled the accessible way: the `href` is removed entirely, so it is neither focusable nor activatable, and
     * `role="link"` + `aria-disabled="true"` keep it announced as a disabled link. It never stays on `href="#"`.
     */
    _updateCallToAction(look, productIndex) {
      var cta = look.querySelector('[data-dstl-cta]');
      if (!cta) {
        return;
      }

      var products = toArray(look.querySelectorAll('[data-dstl-product-item]'));
      var selected = products[productIndex] || products[0] || null;
      var card = selected ? selected.querySelector('[data-dstl-card]') : null;
      var url = card ? card.getAttribute('data-dstl-url') : '';

      if (url) {
        cta.setAttribute('href', url);
        cta.removeAttribute('role');
        cta.removeAttribute('aria-disabled');
      } else {
        cta.removeAttribute('href');
        cta.setAttribute('role', 'link');
        cta.setAttribute('aria-disabled', 'true');
      }
    }

    /* ----------------------------------------------------------------------------------------------------------
       MOBILE FULL SCREEN LAYER
       ---------------------------------------------------------------------------------------------------------- */

    _bindModal() {
      if (!this._modal) {
        return;
      }
      var self = this;
      var modal = this._modal;

      modal.addEventListener(
        'cancel',
        function (event) {
          // Escape: play the leave animation first, then let the dialog actually close.
          if (!self._modalClosing) {
            event.preventDefault();
            self._closeModal();
          }
        },
        { signal: this._signal }
      );

      modal.addEventListener(
        'close',
        function () {
          self._afterModalClosed();
        },
        { signal: this._signal }
      );

      // Keep the hot spots and the product strip in sync while the strip is swiped.
      this._panels.forEach(function (panel, lookIndex) {
        var strip = panel.querySelector('[data-dstl-products]');
        var cells = toArray(panel.querySelectorAll('[data-dstl-product-item]'));
        if (!strip || cells.length < 2) {
          return;
        }
        onScrollSettled(strip, self._signal, function () {
          if (!self._modalOpen || self._lookIndex !== lookIndex) {
            return;
          }
          var index = closestCellIndex(strip, cells);
          if (index !== self._productIndex[lookIndex]) {
            self._selectProduct(lookIndex, index, { fromProductScroll: true });
            self._applyStageTranslate(self._computeStageTranslate(), true);
          }
        });
      });
    }

    _openModal(lookIndex) {
      var modal = this._modal;
      if (!modal || this._modalOpen) {
        return;
      }

      lookIndex = clamp(lookIndex, 0, this._lookCount - 1);

      var source = this._mobileLooks[lookIndex] && this._mobileLooks[lookIndex].querySelector('[data-dstl-media]');
      if (!source) {
        return;
      }

      this._lookIndex = lookIndex;
      this._previousFocus = document.activeElement;

      // ---- clone the focused look (image + hot spots) into the top layer -----------------------------------
      var sourceRect = source.getBoundingClientRect();
      var clone = source.cloneNode(true);

      clone.removeAttribute('id');
      toArray(clone.querySelectorAll('[id]')).forEach(function (node) {
        node.removeAttribute('id');
      });
      toArray(clone.querySelectorAll('img')).forEach(function (image) {
        // The original image is still in the page, so the clone is decorative.
        image.setAttribute('alt', '');
        image.setAttribute('loading', 'eager');
      });
      toArray(clone.querySelectorAll('[data-dstl-hotspot]')).forEach(function (hotspot) {
        // These no longer open anything — they only move the selection inside the already-open layer.
        hotspot.removeAttribute('aria-controls');
        hotspot.removeAttribute('aria-haspopup');
      });

      this._modalStage.replaceChildren(clone);

      this._panels.forEach(function (panel, index) {
        panel.hidden = index !== lookIndex;
      });

      lockPageScroll();
      this._modalOpen = true;
      this._modalClosing = false;

      if (this._supportsDialog) {
        modal.showModal();
      } else {
        // Very old browsers: no top layer available. Fall back to a maximum z-index fixed overlay.
        modal.setAttribute('open', '');
        modal.classList.add('dstl-modal--fallback');
        this._bindFallbackEscape();
        var closeButton = modal.querySelector('[data-dstl-close]');
        if (closeButton) {
          closeButton.focus();
        }
      }

      // Align the inline strip with the look we are showing, and settle the product selection.
      scrollCellIntoView(this._strip, this._mobileLooks[lookIndex], false);
      this._selectProduct(lookIndex, this._productIndex[lookIndex] || 0, { instant: true });

      // ---- FLIP: zoom from the inline thumbnail up to the full width layer --------------------------------
      this._modalStage.style.transform = 'none';
      var naturalRect = this._modalStage.getBoundingClientRect();
      var translateY = this._computeStageTranslate();
      this._applyStageTranslate(translateY, false);

      var scale = naturalRect.width > 0 ? sourceRect.width / naturalRect.width : 1;
      var deltaX = sourceRect.left - naturalRect.left;
      var deltaY = sourceRect.top - naturalRect.top - translateY;

      this._track(animateElements(modal, { opacity: [0, 1] }, { duration: 250 }));

      this._stageAnimation = animateElements(
        this._modalStage,
        {
          transform: [
            'translateY(' + translateY + 'px) translate(' + deltaX + 'px, ' + deltaY + 'px) scale(' + scale + ')',
            'translateY(' + translateY + 'px)'
          ]
        },
        { duration: 400, easing: EASE_IN_OUT_CUBIC }
      );

      this._track(
        animateElements(
          this._modalSheet,
          { transform: ['translateY(100%)', 'translateY(0)'] },
          { duration: 300, easing: EASE_IN_OUT_CUBIC }
        )
      );
    }

    /**
     * Vertical offset that puts the selected hot spot in the middle of the space left above the product sheet.
     * Same intent as the original `changeLookFocalPoint`, expressed in the modal's own coordinate space instead
     * of the page flow, and driven by the hot spot's authored percentage rather than measured offsets.
     */
    _computeStageTranslate() {
      if (!this._modalOpen || !this._modalStage) {
        return 0;
      }

      var media = this._modalStage.firstElementChild;
      if (!media) {
        return 0;
      }

      // Measure without any transform applied.
      if (this._stageAnimation) {
        this._stageAnimation.cancel();
        this._stageAnimation = null;
      }
      var restore = this._modalStage.style.transform;
      this._modalStage.style.transform = 'none';

      var mediaHeight = media.getBoundingClientRect().height;
      var sheetHeight = this._modalSheet ? this._modalSheet.getBoundingClientRect().height : 0;
      var available = Math.max(window.innerHeight - sheetHeight, 0);

      this._modalStage.style.transform = restore;

      var hotspot = this._modalStage.querySelector('[data-dstl-hotspot][aria-current="true"]');
      var percent = hotspot ? parseFloat(hotspot.getAttribute('data-dstl-y')) : 50;
      if (isNaN(percent)) {
        percent = 50;
      }

      if (mediaHeight <= available) {
        return Math.round((available - mediaHeight) / 2);
      }

      return Math.round(clamp(available / 2 - mediaHeight * (percent / 100), available - mediaHeight, 0));
    }

    _applyStageTranslate(translateY, animated) {
      if (!this._modalStage) {
        return;
      }

      var previous = this._stageTranslateY;
      this._stageTranslateY = translateY;

      if (this._stageAnimation) {
        this._stageAnimation.cancel();
        this._stageAnimation = null;
      }

      this._modalStage.style.transform = 'translateY(' + translateY + 'px)';

      if (animated && previous !== translateY) {
        this._stageAnimation = animateElements(
          this._modalStage,
          { transform: ['translateY(' + previous + 'px)', 'translateY(' + translateY + 'px)'] },
          { duration: 400, easing: EASE_IN_OUT_CUBIC }
        );
      }
    }

    _closeModal() {
      var modal = this._modal;
      if (!modal || !this._modalOpen || this._modalClosing) {
        return;
      }
      this._modalClosing = true;

      var self = this;
      this._cancelRunning();

      var translateY = this._stageTranslateY;
      this._modalStage.style.transform = 'translateY(' + translateY + 'px)';

      // Reverse the zoom back onto the inline thumbnail (re-measured, in case the viewport changed).
      var source =
        this._mobileLooks[this._lookIndex] && this._mobileLooks[this._lookIndex].querySelector('[data-dstl-media]');
      var stageRect = this._modalStage.getBoundingClientRect();
      var animations = [
        animateElements(
          this._modalSheet,
          { transform: ['translateY(0)', 'translateY(100%)'] },
          { duration: 250, easing: EASE_IN_OUT_CUBIC }
        ),
        animateElements(modal, { opacity: [1, 0] }, { duration: 250, delay: 100 })
      ];

      if (source) {
        var sourceRect = source.getBoundingClientRect();
        var scale = stageRect.width > 0 ? sourceRect.width / stageRect.width : 1;
        var back =
          'translateY(' +
          translateY +
          'px) translate(' +
          (sourceRect.left - stageRect.left) +
          'px, ' +
          (sourceRect.top - stageRect.top) +
          'px) scale(' +
          scale +
          ')';

        animations.push(
          animateElements(
            this._modalStage,
            { transform: ['translateY(' + translateY + 'px)', back] },
            { duration: 300, easing: EASE_IN_OUT_CUBIC }
          )
        );
      }

      Promise.all(
        animations.map(function (control) {
          return control.finished;
        })
      ).then(function () {
        animations.forEach(function (control) {
          control.cancel();
        });
        self._forceCloseModal();
      });
    }

    _forceCloseModal() {
      var modal = this._modal;
      if (!modal) {
        return;
      }

      if (this._supportsDialog) {
        if (modal.open) {
          modal.close(); // fires "close" -> _afterModalClosed
        } else {
          this._afterModalClosed();
        }
      } else {
        modal.removeAttribute('open');
        modal.classList.remove('dstl-modal--fallback');
        this._afterModalClosed();
      }
    }

    _afterModalClosed() {
      if (!this._modalOpen) {
        return;
      }
      this._modalOpen = false;
      this._modalClosing = false;

      if (this._fallbackEscapeController) {
        this._fallbackEscapeController.abort();
        this._fallbackEscapeController = null;
      }

      if (this._stageAnimation) {
        this._stageAnimation.cancel();
        this._stageAnimation = null;
      }

      // Remove every trace: cloned nodes and inline styles.
      if (this._modalStage) {
        this._modalStage.replaceChildren();
        this._modalStage.style.removeProperty('transform');
      }
      if (this._modalSheet) {
        this._modalSheet.style.removeProperty('transform');
      }
      this._modal.style.removeProperty('opacity');
      this._panels.forEach(function (panel) {
        panel.hidden = true;
      });
      this._stageTranslateY = 0;

      unlockPageScroll();

      // Native <dialog> restores focus on its own; doing it explicitly keeps the fallback path identical.
      var previous = this._previousFocus;
      this._previousFocus = null;
      if (previous && document.contains(previous) && typeof previous.focus === 'function') {
        try {
          previous.focus({ preventScroll: true });
        } catch (error) {
          previous.focus();
        }
      }
    }

    _bindFallbackEscape() {
      var self = this;
      this._fallbackEscapeController = new AbortController();
      document.addEventListener(
        'keydown',
        function (event) {
          if (event.key === 'Escape') {
            event.preventDefault();
            self._closeModal();
          }
        },
        { signal: this._fallbackEscapeController.signal }
      );
    }
  }

  window.customElements.define(TAG_NAME, ShopTheLookSection);
})();